import { useState } from "react";
import { toast } from "sonner";
import { moveItem, readJson, uploadMedia } from "./adminApi";

const EMPTY_FORM = { name: "", kind: "text", text: "", rating: 0, featured: false };

const uploadImages = async (files) => {
  const urls = [];
  for (const f of files) {
    const m = await uploadMedia(f);
    if (m.kind !== "image") throw new Error("Please choose image files only");
    urls.push(m.url);
  }
  return urls;
};

const validateNew = (tForm, tFile, tImages) => {
  if (!tForm.name.trim()) return "Name is required";
  if (tForm.kind === "text" && !tForm.text.trim()) return "Testimonial text cannot be empty";
  if ((tForm.kind === "audio" || tForm.kind === "video") && !tFile) return `Please choose a ${tForm.kind} file`;
  if (tForm.kind === "image" && tImages.length === 0) return "Please choose at least one photo";
  return null;
};

export const useTestimonialsAdmin = (authFetch) => {
  const [testimonials, setTestimonials] = useState([]);
  const [tForm, setTForm] = useState(EMPTY_FORM);
  const [tFile, setTFile] = useState(null);
  const [tImages, setTImages] = useState([]);
  const [tDragIdx, setTDragIdx] = useState(null);
  const [tBusy, setTBusy] = useState(false);
  const [editingT, setEditingT] = useState(null);
  const [tEditForm, setTEditForm] = useState({ name: "", text: "", rating: 0, featured: false });
  const [tEditImages, setTEditImages] = useState([]);
  const [tEditDragIdx, setTEditDragIdx] = useState(null);
  const [tEditUploading, setTEditUploading] = useState(false);

  const moveTImage = (from, to) => {
    if (from === null || from === to) return;
    setTImages((imgs) => moveItem(imgs, from, to));
    setTDragIdx(null);
  };
  const moveTEditImage = (from, to) => {
    if (from === null || from === to) return;
    setTEditImages((imgs) => moveItem(imgs, from, to));
    setTEditDragIdx(null);
  };

  const buildPayload = async () => {
    const payload = { name: tForm.name, kind: tForm.kind, text: tForm.text, rating: tForm.rating, featured: tForm.featured };
    if (tForm.kind === "image") {
      const uploaded = await uploadImages(tImages);
      return { ...payload, media_url: uploaded[0], media_urls: uploaded, content_type: "image" };
    }
    if (tForm.kind !== "text") {
      const m = await uploadMedia(tFile);
      return { ...payload, media_url: m.url, content_type: m.content_type };
    }
    return payload;
  };

  const addTestimonial = async (e) => {
    e.preventDefault();
    const err = validateNew(tForm, tFile, tImages);
    if (err) { toast.error(err); return; }
    setTBusy(true);
    try {
      const res = await authFetch(`/admin/testimonials`, { method: "POST", body: JSON.stringify(await buildPayload()) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setTestimonials((t) => [data, ...(data.featured ? t.map((x) => ({ ...x, featured: false })) : t)]);
      setTForm(EMPTY_FORM);
      setTFile(null);
      setTImages([]);
      toast.success("Testimonial added");
    } catch (err2) {
      toast.error(err2.message || "Failed to add testimonial");
    } finally {
      setTBusy(false);
    }
  };

  const patch = async (id, body, unfeatureOthers) => {
    const res = await authFetch(`/admin/testimonials/${id}`, { method: "PUT", body: JSON.stringify(body) });
    if (!res.ok) throw new Error();
    const updated = await res.json();
    setTestimonials((ts) => ts.map((x) => (x.id === updated.id ? updated : (unfeatureOthers ? { ...x, featured: false } : x))));
  };

  const toggleTestimonialVisible = async (t) => {
    try { await patch(t.id, { visible: !(t.visible !== false) }, false); }
    catch { toast.error("Update failed"); }
  };

  const toggleFeatured = async (t) => {
    const makeFeatured = !t.featured;
    try {
      await patch(t.id, { featured: makeFeatured }, makeFeatured);
      toast.success(makeFeatured ? "Marked as featured" : "Removed from featured");
    } catch { toast.error("Update failed"); }
  };

  const startEditT = (t) => {
    setEditingT(t);
    setTEditForm({ name: t.name || "", text: t.text || "", rating: t.rating || 0, featured: !!t.featured });
    setTEditImages(t.kind === "image" ? (t.media_urls?.length ? [...t.media_urls] : (t.media_url ? [t.media_url] : [])) : []);
    setTEditDragIdx(null);
  };

  const addEditPhotos = async (files) => {
    if (!files.length) return;
    setTEditUploading(true);
    try {
      const urls = await uploadImages(files);
      setTEditImages((imgs) => [...imgs, ...urls]);
      toast.success(`${urls.length} photo(s) added`);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setTEditUploading(false);
    }
  };

  const saveEditT = async (e) => {
    e.preventDefault();
    if (editingT.kind === "image" && tEditImages.length === 0) { toast.error("A photo testimonial needs at least one photo"); return; }
    try {
      const payload = { ...tEditForm };
      if (editingT.kind === "image") payload.media_urls = tEditImages;
      await patch(editingT.id, payload, tEditForm.featured);
      toast.success("Testimonial updated");
      setEditingT(null);
    } catch { toast.error("Update failed"); }
  };

  const deleteTestimonial = async (t) => {
    if (!window.confirm(`Delete testimonial by ${t.name}?`)) return;
    try {
      const res = await authFetch(`/admin/testimonials/${t.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setTestimonials((ts) => ts.filter((x) => x.id !== t.id));
      toast.success("Testimonial deleted");
    } catch { toast.error("Delete failed"); }
  };

  return {
    testimonials, setTestimonials, tForm, setTForm, tFile, setTFile, tImages, setTImages, tDragIdx, setTDragIdx, moveTImage, addTestimonial, tBusy,
    toggleFeatured, toggleTestimonialVisible, deleteTestimonial,
    editingT, setEditingT, tEditForm, setTEditForm, tEditImages, setTEditImages, tEditDragIdx, setTEditDragIdx, moveTEditImage, addEditPhotos, tEditUploading, startEditT, saveEditT,
  };
};
